import React, { useEffect, useRef, useState } from 'react';
import { Mic, Square, Send, X, Loader2 } from 'lucide-react';
import { Button } from '@easyflow/ui-frontend/components/ui/button';

// Task #1660 — Voice message recorder (max 2 min).
// Usa MediaRecorder API standard. Output:
//   - Chromium/Firefox: audio/webm;codecs=opus
//   - Safari iOS:       audio/mp4;codecs=mp4a.40.2
// Hard cap a 120s (auto-stop), counter visivo. Permission denied → callback
// onError per messaggio in chat.

interface Props {
  onSend: (blob: Blob, durationMs: number) => Promise<void> | void;
  onError?: (msg: string) => void;
  disabled?: boolean;
}

const MAX_MS = 120_000;

function pickMime(): string {
  const candidates = [
    'audio/webm;codecs=opus',
    'audio/webm',
    'audio/mp4;codecs=mp4a.40.2',
    'audio/mp4',
  ];
  for (const c of candidates) {
    if (typeof MediaRecorder !== 'undefined' && MediaRecorder.isTypeSupported(c)) return c;
  }
  return 'audio/webm';
}

export function VoiceRecorder({ onSend, onError, disabled }: Props) {
  const [recording, setRecording] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [preview, setPreview] = useState<{ blob: Blob; durationMs: number; url: string } | null>(null);
  const [sending, setSending] = useState(false);
  const recRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const streamRef = useRef<MediaStream | null>(null);
  const startTsRef = useRef<number>(0);
  const tickRef = useRef<number | null>(null);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (tickRef.current) window.clearInterval(tickRef.current);
      streamRef.current?.getTracks().forEach((t) => t.stop());
      if (preview?.url) URL.revokeObjectURL(preview.url);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- pre-esistente senza motivazione, descrizione aggiunta meccanicamente (#3359)
  }, []);

  const stopStream = () => {
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
  };

  const start = async () => {
    if (disabled || recording) return;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const mime = pickMime();
      const rec = new MediaRecorder(stream, { mimeType: mime });
      chunksRef.current = [];
      rec.ondataavailable = (e) => {
        if (e.data && e.data.size > 0) chunksRef.current.push(e.data);
      };
      rec.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: mime });
        const durationMs = Date.now() - startTsRef.current;
        const url = URL.createObjectURL(blob);
        setPreview({ blob, durationMs, url });
        stopStream();
      };
      rec.start();
      recRef.current = rec;
      startTsRef.current = Date.now();
      setRecording(true);
      setElapsed(0);
      tickRef.current = window.setInterval(() => {
        const e = Date.now() - startTsRef.current;
        setElapsed(e);
        if (e >= MAX_MS) stop();
      }, 200);
    } catch (err) {
      onError?.((err as Error).message || 'Microfono non disponibile');
      stopStream();
    }
  };

  const stop = () => {
    if (tickRef.current) { window.clearInterval(tickRef.current); tickRef.current = null; }
    if (recRef.current && recRef.current.state !== 'inactive') {
      try { recRef.current.stop(); } catch { /* noop */ }
    }
    setRecording(false);
  };

  const discard = () => {
    if (preview?.url) URL.revokeObjectURL(preview.url);
    setPreview(null);
    setElapsed(0);
  };

  const send = async () => {
    if (!preview) return;
    setSending(true);
    try {
      await onSend(preview.blob, preview.durationMs);
      discard();
    } finally {
      setSending(false);
    }
  };

  const mmss = (ms: number) => {
    const s = Math.floor(ms / 1000);
    return `${String(Math.floor(s / 60)).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`;
  };

  if (preview) {
    return (
      <div className="flex items-center gap-2 px-3 py-2 rounded-xl bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-900">
        <audio src={preview.url} controls className="h-8 flex-1 min-w-0" />
        <span className="text-[10px] font-mono text-muted-foreground">{mmss(preview.durationMs)}</span>
        <Button size="icon" variant="ghost" className="h-8 w-8 rounded-lg" onClick={discard} disabled={sending} aria-label="Scarta">
          <X className="h-4 w-4" />
        </Button>
        <Button size="icon" className="h-8 w-8 rounded-lg bg-blue-600 hover:bg-blue-700" onClick={send} disabled={sending} aria-label="Invia vocale">
          {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
        </Button>
      </div>
    );
  }

  if (recording) {
    return (
      <div className="flex items-center gap-2 px-3 py-2 rounded-xl bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-900">
        <span className="h-2 w-2 rounded-full bg-red-500 animate-pulse" />
        <span className="text-xs font-medium text-red-600 dark:text-red-400">Registrazione…</span>
        <span className="text-[10px] font-mono text-muted-foreground flex-1">{mmss(elapsed)} / 02:00</span>
        <Button size="icon" variant="ghost" className="h-8 w-8 rounded-lg" onClick={stop} aria-label="Stop registrazione">
          <Square className="h-4 w-4 text-red-500" />
        </Button>
      </div>
    );
  }

  return (
    <button
      type="button"
      onClick={start}
      disabled={disabled}
      className="h-8 w-8 md:h-9 md:w-9 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 flex items-center justify-center transition-colors disabled:opacity-40"
      title="Registra messaggio vocale (max 2 min)"
      aria-label="Registra vocale"
    >
      <Mic className="h-4 w-4 text-gray-400" />
    </button>
  );
}
